import { ChangeDetectorRef, Component, effect, HostBinding, inject, input, OnChanges, OnDestroy, OnInit, signal } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup } from '@angular/forms';
import { filter, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FlexiLogicResult } from '@customer-apps/flexi-logic';
import { SlotType } from '../../enums';
import { TemplateSlot, DataSourceState, TranslationDescriptor } from '../../interfaces';
import { ContextModelService, DataSourceService, QuestionFlowService } from '../../services';

/**
 * Generic slot component
 */
@Component({
    selector: 'ca-slot',
    template: ''
})
export class SlotComponent implements ControlValueAccessor, OnChanges, OnInit, OnDestroy {
    @HostBinding('class.ca-slot') get class() {
        return !this.isHidden;
    }
    formGroup = new FormGroup({});
    control = new FormControl();
    form = input<FormGroup>();
    markAsTouched = input<boolean>(false);
    slot = input.required<TemplateSlot>();
    slotIndex = input.required<number>();
    questionBlockId = input.required<string>();
    questionBlockValidations = input<any[] | undefined>();
    devMode = input<boolean>(false);

    dataSourceState$: Observable<DataSourceState>;

    protected value = signal<any>(null);

    private dataSourceService = inject(DataSourceService);
    private contextModelService = inject(ContextModelService);
    private questionFlowService = inject(QuestionFlowService);
    private cdr = inject(ChangeDetectorRef);
    private destroy$ = new Subject<void>();

    private readonly errorEffect = effect(() => {
        void this.value();
        this.setError();
    });

    private readonly disabledEffect = effect(() => {
        const slotId = this.slot()?.id;
        if (!slotId || !this.questionFlowService) {
            this.control.enable();
            return;
        }
        const disabled = this.questionFlowService.getSlotDisabledSignal(slotId)();
        if (disabled) {
            this.control.disable();
        } else {
            this.control.enable();
        }
        this.cdr.markForCheck();
    });

    private readonly markAsTouchedEffect = effect(() => {
        const markAsTouched = this.markAsTouched();
        if (markAsTouched) {
            this.formGroup.markAsTouched();
            this.control.markAsTouched();
        }
    });

    ngOnChanges() {
        this.isDisabled ? this.control.disable({ emitEvent: false }) : this.control.enable({ emitEvent: false });
    }

    ngOnInit() {
        this.initDataSource();
        this.setUpFormControl();
        this.onFormValueChange();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onChange(value: any): void {}

    onTouched(): void {}

    writeValue(value: any): void {}

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = (value: any) => {
            this.selectDataSourceItem(value);
            fn(value);
        };
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = () => {
            fn();
        };
    }

    get titleData(): FlexiLogicResult | null {
        if (!this.slot()?.titleData) {
            return false;
        }
        return this.contextModelService.processJsonLogic(this.slot().titleData!);
    }

    get dataSourceMapping(): FlexiLogicResult | null {
        if (!this.slot()?.dataSourceMapping) {
            return false;
        }
        return this.contextModelService.processJsonLogic(this.slot().dataSourceMapping!);
    }

    get isInactive(): FlexiLogicResult | null {
        if (!this.slot()?.isInactive) {
            return false;
        }
        return this.contextModelService.processJsonLogic(this.slot().isInactive!);
    }

    get errorMessageTranslation(): TranslationDescriptor | null {
        const validations = [...(this.slot().validations || []), ...(this.questionBlockValidations() || [])];
        const validation = validations.find(v => {
            return !this.contextModelService.processJsonLogic(v.logicalCondition);
        });
        return validation ? { key: validation?.errorMessageKey, data: validation?.errorMessageData } : null;
    }

    get isHidden(): FlexiLogicResult | null {
        if (!this.slot()?.isHidden) {
            return false;
        }
        return this.contextModelService.processJsonLogic(this.slot().isHidden!);
    }

    get isDisabled(): FlexiLogicResult | null {
        if (!this.slot()?.isDisabled) {
            return false;
        }
        return this.contextModelService.processJsonLogic(this.slot().isDisabled!);
    }

    get isOptional(): boolean {
        return !!this.slot()?.isOptional;
    }

    get isVisual(): boolean {
        return !!this.slot()?.visual;
    }

    get isErrorMessage(): boolean {
        const control = this.isFormGroupNeeded ? this.formGroup : this.control;
        const invalid = control.invalid && control.touched;
        return invalid && !!this.errorMessageTranslation;
    }

    get numericMin(): number | undefined {
        if (!this.slot()?.numericMin) {
            return undefined;
        }
        return this.contextModelService.processJsonLogic(this.slot().numericMin!) as number;
    }

    get numericMax(): number | undefined {
        if (!this.slot()?.numericMax) {
            return undefined;
        }
        return this.contextModelService.processJsonLogic(this.slot().numericMax!) as number;
    }

    get numericStep(): number | undefined {
        if (!this.slot()?.numericStep) {
            return undefined;
        }
        return this.contextModelService.processJsonLogic(this.slot().numericStep!) as number;
    }

    /**
     * FormGroup is needed when the slot has a data source and is of type CheckboxInlineSlot, CheckboxTileSlot
     * or MultiSelectDropdownSlot as these types require multiple controls for their options.
     * For other slot types or when there is no data source, a single FormControl is sufficient.
     */
    protected get isFormGroupNeeded(): boolean {
        return (
            !!this.slot()?.dataSource &&
            [SlotType.CheckboxInlineSlot, SlotType.CheckboxTileSlot, SlotType.MultiselectDropdownSlot].includes(this.slot().type)
        );
    }

    private initDataSource(): void {
        if (!this.slot()?.dataSource!) {
            return;
        }

        this.dataSourceService
            .getDataSourceItems(this.slot().dataSource!)
            .pipe(
                tap(data => {
                    if (!this.isFormGroupNeeded || !data?.length) {
                        return;
                    }
                    data.filter(item => !this.formGroup.contains(`${item.value}`)).forEach(item => {
                        this.formGroup.addControl(`${item.value}`, new FormControl());
                    });
                }),
                switchMap(() => this.dataSourceService.dataSourcesCalled$),
                filter(Boolean),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                const value = this.value();
                this.selectDataSourceItem(value);
                if (this.isFormGroupNeeded) {
                    const selectedValues = Array.isArray(value) ? value : [];
                    const formGroupValue: { [key: string]: boolean } = {};

                    Object.keys(this.formGroup.controls).forEach(key => {
                        formGroupValue[key] = selectedValues.includes(key);
                    });

                    this.formGroup.setValue(formGroupValue);
                } else {
                    this.control.setValue(value);
                }
                this.setError();
            });

        this.dataSourceState$ = this.dataSourceService.getDataSourceState(this.slot().dataSource!);
    }

    /**
     * The control is removed and re-added to the form to ensure that form control/group has the correct reference.
     * It's needed for form validation and value tracking.
     */
    private setUpFormControl() {
        this.form()?.removeControl(this.slot().id);
        if (this.isFormGroupNeeded) {
            this.form()?.addControl(this.slot().id, this.formGroup);
        } else {
            this.form()?.addControl(this.slot().id, this.control);
        }
    }

    private selectDataSourceItem(value: any): void {
        if (!this.slot()?.dataSource) {
            return;
        }
        this.contextModelService.selectDataSourceItem(this.slot(), value);
    }

    protected setError() {
        const error = this.errorMessageTranslation ? { error: this.errorMessageTranslation } : null;
        const control = this.isFormGroupNeeded ? this.formGroup : this.control;
        control.setErrors(error);
    }

    private onFormValueChange(): void {
        this.form()
            ?.valueChanges.pipe(
                tap(() => this.setError()),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }
}
